import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, brandIntro, teamMembers, certifications, brandHighlights } from '@/mocks/siteInfo';
import { FORM_URLS } from '@/utils/formUrls';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 17.3关于性教育宣传片',
    description: siteInfo.description,
    url: 'https://www.173sexeducation.org/about',
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      foundingDate: siteInfo.foundingYear,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema, null, 2) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Page Header */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              了解 17.3 性教育宣传片背后的故事、使命与团队
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  我们的使命
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-5">
                  {brandIntro.mission}
                </p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  我们的愿景
                </h2>
                <p className="text-foreground-700 leading-relaxed">
                  {brandIntro.vision}
                </p>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20illustration%20of%20diverse%20educators%20and%20students%20in%20a%20bright%20modern%20educational%20setting%2C%20collaborative%20learning%20atmosphere%2C%20soft%20natural%20light%2C%20inclusive%20and%20supportive%20environment%2C%20editorial%20photography%20style&width=800&height=600&seq=about-mission-01&orientation=landscape"
                  alt="我们的使命与愿景"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {brandHighlights.map((item) => (
                <div key={item.label} className="text-center p-5 rounded-xl bg-background-100">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {item.number}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* History Timeline */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-primary-200 transform md:-translate-x-px" />
              <div className="space-y-8">
                {brandIntro.history.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-center pl-10 md:pl-0 md:justify-end">
                      <div className={`md:text-right ${index % 2 === 0 ? '' : 'md:text-left md:pl-8'}`}>
                        <div className="flex items-center gap-3 md:justify-end mb-1">
                          <span className="text-2xl font-heading font-bold text-primary-600">{item.year}</span>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full transform -translate-x-1.5 mt-1.5 ring-4 ring-primary-100 z-10" />
                    <div className="md:w-1/2 pl-10 md:pl-8">
                      <p className="text-sm text-foreground-700 leading-relaxed bg-background-50 p-4 rounded-lg">
                        {item.event}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              专家团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-100 rounded-xl p-6 text-center transition-all duration-300 hover:scale-[1.02]">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                    <i className="ri-user-3-line text-2xl text-primary-600" />
                  </div>
                  <h4 className="font-heading font-semibold text-foreground-950 mb-1">{member.name}</h4>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              资质与认可
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {certifications.map((cert) => (
                <div key={cert} className="bg-background-50 rounded-xl p-5 flex items-center gap-3 transition-all duration-300 hover:scale-[1.02]">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-accent-600 text-lg" />
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{cert}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
              联系我们
            </h2>
            <form
                data-readdy-form="true"
                action={FORM_URLS.contact}
                method="POST"
                encType="application/x-www-form-urlencoded"
                className="space-y-5"
                id="contact-form"
              >
                <div className="anti-spam-field">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                    placeholder="请输入咨询主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言（500字以内）"
                  />
                  <p className="text-xs text-foreground-400 mt-1">最多可输入 500 个字符</p>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-lg transition-all duration-300 hover:scale-[1.01] whitespace-nowrap"
                >
                  发送留言
                  <i className="ri-send-plane-line ml-2" />
                </button>
              </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}